<template>
  <div>
    <!-- TODO: Metered Billing full release: remove -->
    <template v-if="isMeteredBillingPartiallyReleased">
      <template v-if="isShowingInformational">
        <wa-message
          v-if="usagePercentage > 100"
          icon-style="solid"
          icon-name="faSkullCrossbones"
          theme="danger"
          type="flash"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Dang it.</strong>
              You've used more than the
              <template v-if="type === 'kit'">pageviews</template>
              <template v-else-if="type === 'npm'">npm bandwidth</template>
              allowed for your plan this month.
            </p>
          </div>
        </wa-message>

        <wa-message
          v-else-if="usagePercentage > 85"
          icon-style="solid"
          icon-name="faExclamationTriangle"
          theme="warning"
          type="flash"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Eek!</strong>
              You're almost over the
              <template v-if="type === 'kit'">pageviews</template>
              <template v-else-if="type === 'npm'">npm bandwidth</template>
              allowed for your plan for this month.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <site-link
                :to="{ name: 'plans' }"
                class="margin-right-md"
                @click.native="$posthog.capture('usage:plans_link_click')"
              >
                Upgrade
              </site-link>
              <site-link
                v-if="type === 'kit'"
                :to="$docs('/web/setup/use-kit', { hash: '#tune-your-kits' })"
                @click.native="$posthog.capture('usage:tune_your_kits_link_click')"
              >
                Learn How to Tune Your Kits
              </site-link>
              <site-link
                v-else-if="type === 'npm'"
                :to="$docs('/web/dig-deeper/continuous-integration')"
                @click.native="$posthog.capture('usage:tune_your_ci_link_click')"
              >
                Learn How to Tune Your CI
              </site-link>
            </p>
          </div>
        </wa-message>

        <wa-message
          v-else-if="usagePercentage > 65"
          icon-style="solid"
          icon-name="faBlockQuestion"
          theme="info"
          type="flash"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Just a heads up.</strong>
              You've used over half of your month's
              <template v-if="type === 'kit'">pageview</template>
              <template v-else-if="type === 'npm'">npm bandwidth</template>
              limit.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <site-link
                v-if="type === 'kit'"
                :to="$docs('/web/setup/use-kit', { hash: '#tune-your-kits' })"
                @click.native="$posthog.capture('usage:tune_your_kits_link_click')"
              >
                Learn How to Tune Your Kits
              </site-link>
              <site-link
                v-else-if="type === 'npm'"
                :to="$docs('/web/dig-deeper/continuous-integration')"
                @click.native="$posthog.capture('usage:tune_your_ci_link_click')"
              >
                Learn How to Tune Your CI
              </site-link>
            </p>
          </div>
        </wa-message>
      </template>
    </template>

    <template v-else>
      <!-- Service Deactivated -->
      <template v-if="isShowingDire && isServiceDeactivated">
        <!-- with Billing Limit reached -->
        <wa-message
          v-if="isMeteredBilling"
          icon-style="solid"
          icon-name="faExclamationTriangle"
          theme="danger"
          :title="`You've used your plan's monthly ${serviceUnitLabel} limit and Metered Billing limit.`"
          :class="{ 'margin-top-2xl': !isShowingAllMessageGroups }"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              We've deactivated this service until it resets next month -
              {{ deactivationRepercussion }}. Increase your overage billing limit to reactivate it immediately.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!-- with Plan limit exceeded with billable usage -->
        <wa-message
          v-else-if="billablePageviewsOverage"
          icon-style="solid"
          icon-name="faExclamationTriangle"
          theme="danger"
          :class="{ 'margin-top-2xl': !isShowingAllMessageGroups }"
        >
          <div slot="content">
            <p>
              <strong>You've used your plan's monthly {{ serviceUnitLabel }} limit and have metered overages.</strong>
            </p>
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              We've deactivated this service until it resets next month - {{ deactivationRepercussion }}. You'll also be billed for this month's existing
              overage charges.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action v-if="!$dt.isFree && isMeteredBillingFeatureOn" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!-- with Plan limit exceeded, but not yet with a billable level of usage -->
        <wa-message
          v-else
          icon-style="solid"
          icon-name="faExclamationTriangle"
          theme="danger"
          :title="`You've used your plan's monthly ${serviceUnitLabel} limit`"
          :class="{ 'margin-top-2xl': !isShowingAllMessageGroups }"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              We've deactivated this service until it resets next month -
              {{ deactivationRepercussion }}.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action v-if="!$dt.isFree && isMeteredBillingFeatureOn" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>
      </template>

      <!-- Metered Billing with Billing Limit -->
      <template v-else-if="isShowingInformational && isMeteredBilling && hasSetOverageLimit && overageUsagePercentage < 100">
        <!--  Billing Limit > 90% met -->
        <wa-message
          v-if="overageUsagePercentage >= 90"
          icon-style="solid"
          icon-name="faExclamationTriangle"
          theme="warning"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Eek!</strong>
              You're almost at your
              {{ serviceUnitLabel }}
              metered billing limit. Increase your overage billing limit to avoid any service interruptions.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!--  Billing Limit > 60% met -->
        <wa-message
          v-else-if="overageUsagePercentage >= 60"
          icon-style="solid"
          icon-name="faExclamationTriangle"
          theme="warning"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Heads up!</strong>
              You've used over half of your
              {{ serviceUnitLabel }}
              metered billing limit. You can increase the overage billing limit to avoid any service interruptions.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!-- 100% of Plan limit or greater use -->
        <wa-message
          v-else-if="usagePercentage >= 100"
          icon-style="solid"
          icon-name="faCircleInfo"
          theme="info"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong> You've used your plan's monthly {{ serviceUnitLabel }}</strong>
              and will be charged for any metered billing overages until {{ overagesTerminationMomentLabel }}.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!-- 90% of Plan limit or greater use -->
        <wa-message
          v-else-if="usagePercentage >= 90"
          icon-style="solid"
          icon-name="faCircleInfo"
          theme="info"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>You're almost over your plan's monthly {{ serviceUnitLabel }} limit</strong>. If your use exceeds that, you will be charged for any
              metered billing overages until {{ overagesTerminationMomentLabel }}.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!-- 60% of Plan limit or greater use -->
        <wa-message
          v-else-if="usagePercentage >= 60"
          icon-style="solid"
          icon-name="faCircleInfo"
          theme="info"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Heads up!</strong>
              You've used over half of your Plan's monthly
              {{ serviceUnitLabel }}
              limit.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="false"
                :type="type"
              />
            </p>
          </div>
        </wa-message>
      </template>

      <!-- Metered Billing with no Billing Limit -->
      <template v-else-if="isShowingInformational && isMeteredBilling && !hasSetOverageLimit">
        <!-- TODO: Metered Billing full release: uncomment -->
        <!-- 10X Plan limit use -->
        <wa-message
          v-if="usagePercentage >= 1000"
          icon-style="solid"
          icon-name="faExclamationTriangle"
          theme="warning"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Heads up!</strong>
              You've used over 10x of your plan's monthly {{ serviceUnitLabel }} limit.
              <template v-if="isMeteredBillingFeatureOn">You will be charged for any metered billing overages.</template>
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!-- 100% of Plan limit or greater use -->
        <wa-message
          v-else-if="usagePercentage >= 100"
          icon-style="solid"
          icon-name="faCircleInfo"
          theme="info"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong> You've used your plan's monthly {{ serviceUnitLabel }}</strong>
              and will be charged for any metered billing overages until {{ overagesTerminationMomentLabel }}.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!-- 90% of Plan limit or greater use -->
        <wa-message
          v-else-if="usagePercentage >= 90"
          icon-style="solid"
          icon-name="faCircleInfo"
          theme="info"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>You're almost over your plan's monthly {{ serviceUnitLabel }} limit</strong>. If your use exceeds that, you will be charged for any
              metered billing overages until {{ overagesTerminationMomentLabel }}.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!-- 60% of Plan limit or greater use -->
        <wa-message
          v-else-if="usagePercentage >= 60"
          icon-style="solid"
          icon-name="faCircleInfo"
          theme="info"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Heads up!</strong>
              You've used over half of your Plan's monthly
              {{ serviceUnitLabel }}
              limit.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action action="settings" />
              <usage-actions
                :is-showing-upgrade="false"
                :type="type"
              />
            </p>
          </div>
        </wa-message>
      </template>

      <!-- no Metered Billing -->
      <template v-else-if="isShowingInformational && !isMeteredBilling">
        <!-- 90% of Plan limit or greater use -->
        <wa-message
          v-if="usagePercentage >= 90"
          icon-style="solid"
          icon-name="faExclamationTriangle"
          theme="warning"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Eek!</strong> You're almost over your plan's monthly {{ serviceUnitLabel }} limit.
              <template v-if="!$dt.isFree && isMeteredBillingFeatureOn">Enable metered billing to avoid any service interruptions.</template>
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <metered-billing-action v-if="!$dt.isFree && isMeteredBillingFeatureOn" />
              <usage-actions
                :is-showing-upgrade="isAllowedToUpgrade"
                :type="type"
              />
            </p>
          </div>
        </wa-message>

        <!-- 60% of Plan limit or greater use -->
        <wa-message
          v-else-if="usagePercentage >= 60"
          icon-style="solid"
          icon-name="faCircleInfo"
          theme="info"
        >
          <div slot="content">
            <p style="--paragraph-margin-bottom: var(--spacing-xs)">
              <strong>Heads up!</strong>
              You've used over half of your Plan's monthly
              {{ serviceUnitLabel }}
              limit.
            </p>
            <p style="--paragraph-margin-bottom: 0">
              <usage-actions
                :is-showing-upgrade="false"
                :type="type"
              />
            </p>
          </div>
        </wa-message>
      </template>
    </template>
  </div>
</template>

<script>
  import { BandwidthUsageMixin } from 'app/views/accounts/AccountView/BandwidthMixins'
  import { KitSubscriptionMixin, KitsStatusAndUsageMixin } from 'app/views/kits/mixins'
  import MeteredBillingAction from './MeteredBillingAction'
  import UsageActions from './UsageActions'

  export default {
    name: 'UsageMessages',

    components: {
      MeteredBillingAction,
      UsageActions
    },

    mixins: [BandwidthUsageMixin, KitSubscriptionMixin, KitsStatusAndUsageMixin],

    props: {
      isLast: {
        type: Boolean,
        default: false
      },
      messageGroups: {
        type: Array,
        default: () => {
          return ['dire', 'informational']
        }
      },
      type: {
        type: String,
        required: true
      }
    },

    computed: {
      isAllowedToUpgrade() {
        return !this.$subscription?.plan?.sku === 'FA-MAX'
      },

      deactivationRepercussion() {
        return this.type === 'npm' ? "you won't be able to install packages" : 'icons may stop rendering in your project'
      },

      isMeteredBilling() {
        return this.type === 'npm' ? this.isMeteredBillingBandwidth : this.isMeteredBillingPageviews
      },

      isMeteredBillingFeatureOn() {
        return this.type === 'npm' ? this.isMeteredBillingBandwidthFeatureOn : this.isMeteredBillingPageviewsFeatureOn
      },

      isMeteredBillingPartiallyReleased() {
        return this.type === 'npm' ? this.isMeteredBillingBandwidthPartiallyReleased : this.isMeteredBillingPageviewsPartiallyReleased
      },

      isServiceDeactivated() {
        return this.type === 'npm' ? this.npmTokenDeactivated : this.kitsDeactivated
      },

      isShowingDire() {
        return this.messageGroups.includes('dire')
      },

      isShowingInformational() {
        return this.messageGroups.includes('informational')
      },

      isShowingAllMessageGroups() {
        return this.isShowingDire && this.isShowingInformational
      },

      overagesTerminationMomentLabel() {
        return this.hasSetOverageLimit ? 'the limit you set is reached' : 'it resets next month'
      },

      serviceUnitLabel() {
        return this.type === 'npm' ? 'Package Bandwidth' : 'Pageviews'
      },

      usagePercentage() {
        return this.type === 'npm' ? this.planBandwidthUsagePercentage : this.kitUsagePercentage
      },

      hasSetOverageLimit() {
        return this.type === 'npm' ? this.hasSetBandwidthBillingLimit : this.hasSetPageviewsBillingLimit
      },

      overageUsagePercentage() {
        return this.type === 'npm' ? this.bandwidthUsageOveragePercentage : this.kitPageviewsOverageUsagePercentage
      },

      // TODO: Metered Billing full release: remove
      typeSlug() {
        return this.nameSlug(this.type)
      }
    }
  }
</script>
